/**
 * Client-side password generator for OnPass (PR-25 + readable-passphrase
 * follow-up).
 *
 * Pure, dependency-free, and unit-testable. Randomness comes only from the Web
 * Crypto API (`crypto.getRandomValues`) — never `Math.random`. Character
 * selection uses rejection sampling to avoid modulo bias, guarantees at least
 * one character from each enabled set, then shuffles. The generated value never
 * leaves the browser until the user chooses to push it.
 *
 * Two modes: a random-character generator and a memorable word-passphrase
 * generator (easy to read / type / dictate), both using the same CSPRNG.
 */

import { WORDS } from './wordlist';

export interface PasswordOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  digits: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
}

export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 64;
// "Super strong" mode default — a long random string. Raised from 20 to 40 so
// the strongest option leads with a genuinely strong length out of the box.
export const PASSWORD_DEFAULT = 40;

/** Characters that are easy to confuse, removed when "exclude ambiguous" is on. */
export const AMBIGUOUS = 'O0oIl1|';

const SETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  digits: '0123456789',
  symbols: '!@#$%^&*()-_=+[]{};:,.?',
} as const;

export const DEFAULT_OPTIONS: PasswordOptions = {
  length: PASSWORD_DEFAULT,
  uppercase: true,
  lowercase: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: true,
};

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, Math.trunc(n)));
}

/** A single uniform 32-bit value from the CSPRNG. */
function randomUint32(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0];
}

/**
 * Uniform random integer in [0, maxExclusive) with rejection sampling — no
 * modulo bias.
 */
export function randomInt(maxExclusive: number): number {
  if (maxExclusive <= 0) throw new Error('maxExclusive must be > 0');
  // Largest multiple of maxExclusive that fits in 32 bits; reject above it.
  const limit = Math.floor(0x1_0000_0000 / maxExclusive) * maxExclusive;
  let v = randomUint32();
  while (v >= limit) v = randomUint32();
  return v % maxExclusive;
}

/** The enabled character pools, each already filtered for ambiguity. Empty pools dropped. */
function enabledPools(o: PasswordOptions): string[] {
  const filter = (s: string) =>
    o.excludeAmbiguous ? [...s].filter((c) => !AMBIGUOUS.includes(c)).join('') : s;
  const pools: string[] = [];
  if (o.uppercase) pools.push(filter(SETS.uppercase));
  if (o.lowercase) pools.push(filter(SETS.lowercase));
  if (o.digits) pools.push(filter(SETS.digits));
  if (o.symbols) pools.push(filter(SETS.symbols));
  return pools.filter((p) => p.length > 0);
}

/**
 * Generates a password honouring the options. Guarantees ≥1 character from each
 * enabled set (when length allows), fills the remainder from the combined pool,
 * and shuffles with Fisher–Yates. Throws if no character set is enabled.
 */
export function generatePassword(o: PasswordOptions): string {
  const length = clamp(o.length, PASSWORD_MIN, PASSWORD_MAX);
  const pools = enabledPools(o);
  if (pools.length === 0) throw new Error('Enable at least one character set.');

  const combined = pools.join('');
  const chars: string[] = [];

  // One from each enabled set first (capped at length).
  for (const pool of pools) {
    if (chars.length < length) chars.push(pool[randomInt(pool.length)]);
  }
  // Fill the rest from the combined pool.
  while (chars.length < length) {
    chars.push(combined[randomInt(combined.length)]);
  }
  // Fisher–Yates shuffle so the guaranteed chars aren't positionally predictable.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

/**
 * Estimated entropy in bits: length × log2(poolSize). Monotonic in both length
 * and charset size. Returns 0 when no set is enabled.
 */
export function estimateEntropyBits(o: PasswordOptions): number {
  const length = clamp(o.length, PASSWORD_MIN, PASSWORD_MAX);
  const poolSize = enabledPools(o).join('').length;
  if (poolSize === 0) return 0;
  return Math.round(length * Math.log2(poolSize));
}

/** Human-friendly strength label from the entropy estimate. */
export function strengthLabel(bits: number): 'Weak' | 'Fair' | 'Strong' | 'Very strong' {
  if (bits < 50) return 'Weak';
  if (bits < 75) return 'Fair';
  if (bits < 110) return 'Strong';
  return 'Very strong';
}

// ── Memorable word passphrase (easy to read / type / dictate) ────────────────

export interface PassphraseOptions {
  words: number;        // how many words
  separator: string;    // joins the words (and the trailing extras)
  capitalise: boolean;  // capitalise the first letter of each word
  includeNumber: boolean; // append a random 2-digit number
  includeSymbol: boolean; // append a random symbol
}

export const PASSPHRASE_MIN_WORDS = 3;
export const PASSPHRASE_MAX_WORDS = 8;
export const PASSPHRASE_DEFAULT_WORDS = 4;

/** Symbols safe to read aloud / type, used for the optional passphrase suffix. */
const PASSPHRASE_SYMBOLS = '!@#$%&*?';

export const DEFAULT_PASSPHRASE_OPTIONS: PassphraseOptions = {
  words: PASSPHRASE_DEFAULT_WORDS,
  separator: '-',
  capitalise: false,
  includeNumber: true,
  includeSymbol: false,
};

function capitaliseWord(w: string): string {
  return w.length === 0 ? w : w[0].toUpperCase() + w.slice(1);
}

/**
 * Generates a memorable passphrase, e.g. <c>brave-otter-lamp-7</c>. Words are
 * drawn uniformly (rejection-sampled) from the curated {@link WORDS} list via
 * the CSPRNG; an optional number and/or symbol are appended after the separator.
 */
export function generatePassphrase(o: PassphraseOptions): string {
  const count = clamp(o.words, PASSPHRASE_MIN_WORDS, PASSPHRASE_MAX_WORDS);
  const sep = o.separator ?? '-';

  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const word = WORDS[randomInt(WORDS.length)];
    parts.push(o.capitalise ? capitaliseWord(word) : word);
  }
  if (o.includeNumber) parts.push(String(10 + randomInt(90)));            // 10–99
  if (o.includeSymbol) parts.push(PASSPHRASE_SYMBOLS[randomInt(PASSPHRASE_SYMBOLS.length)]);

  return parts.join(sep);
}

/**
 * Estimated entropy in bits for a passphrase: words × log2(list size) plus the
 * optional number (~6.5 bits for 10–99) and symbol. Monotonic in word count.
 */
export function estimatePassphraseEntropyBits(o: PassphraseOptions): number {
  const count = clamp(o.words, PASSPHRASE_MIN_WORDS, PASSPHRASE_MAX_WORDS);
  let bits = count * Math.log2(WORDS.length);
  if (o.includeNumber) bits += Math.log2(90);
  if (o.includeSymbol) bits += Math.log2(PASSPHRASE_SYMBOLS.length);
  return Math.round(bits);
}

// ── Memorable temporary password (OnPass default) ────────────────────────────
//
// A short, human-friendly pattern for handing a temporary credential to a
// person (easy to read aloud / type): one 5-letter word with its first letter
// capitalised, one special character, then two digits — e.g. "Brave!23".
// Deliberately lower entropy than the random-character mode; acceptable for a
// one-time, expiring, view-limited, link-gated OnPass secret. Symbols are
// restricted to a small, keyboard-friendly, unambiguous set per the OnPass spec.

/** The only special characters allowed in the memorable pattern. */
export const MEMORABLE_SYMBOLS = '!@#*';

/** The 5-letter subset of the curated wordlist, for the memorable pattern. */
const FIVE_LETTER_WORDS: readonly string[] = WORDS.filter((w) => w.length === 5);

/**
 * Generates a memorable temporary password, e.g. `Brave!23`: a capitalised
 * 5-letter word + one {@link MEMORABLE_SYMBOLS} character + two digits (00–99,
 * zero-padded). CSPRNG-backed via {@link randomInt}.
 */
export function generateMemorable(): string {
  const word = capitaliseWord(FIVE_LETTER_WORDS[randomInt(FIVE_LETTER_WORDS.length)]);
  const symbol = MEMORABLE_SYMBOLS[randomInt(MEMORABLE_SYMBOLS.length)];
  const digits = String(randomInt(100)).padStart(2, '0');
  return `${word}${symbol}${digits}`;
}

/**
 * Estimated entropy for the memorable pattern: log2(5-letter words) + the
 * symbol choice + two digits (100). Honest and modest by design.
 */
export function estimateMemorableEntropyBits(): number {
  return Math.round(
    Math.log2(FIVE_LETTER_WORDS.length) + Math.log2(MEMORABLE_SYMBOLS.length) + Math.log2(100),
  );
}
